import { useAuth } from "../context/AuthContext";

// Subscription plan definitions based on pricing structure
export const SUBSCRIPTION_PLANS = {
  FREE: "free",
  PRO: "pro",
  TEAM: "team",
  ENTERPRISE: "enterprise",
};

// Feature definitions based on pricing page
export const PLAN_FEATURES = {
  [SUBSCRIPTION_PLANS.FREE]: {
    dashboard: false,
    basicSales: true,
    basicPurchases: true,
    basicInventory: true,
    invoices: false,
    quotes: "view",
    purchaseOrders: false,
    basicReports: true,
    pdfExport: true,
    excelExport: false,
    userPermissions: false,
    attachments: true,
    aiFeatures: false,
    analytics: false,
    customerAging: false,
    inventoryValuation: false,
    advancedReports: false,
    collaboration: false,
    approvalWorkflows: false,
    maxUsers: 1,
    canEditCategories: false,
    canEditUnits: false,
    canEditInventory: false,
    basicLedger: true,
    requisitions: "create",
    securitySettings: false,
    // Reports access
    trialBalance: "view",
    balanceSheet: false,
    profitLoss: false,
    postings: false,
    cashflow: false,
    chartOfAccounts: false,
    taxReports: false,
    vat: false,
    // Report Categories
    salesReports: false,
    purchaseReports: false,
    inventoryReports: false,
    financialReports: "basic", // Only trial balance
    taxationReports: false,
    customerReports: false,
    vendorReports: false,
  },
  [SUBSCRIPTION_PLANS.PRO]: {
    dashboard: true,
    basicSales: true,
    basicPurchases: true,
    basicInventory: true,
    invoices: true,
    quotes: "full",
    purchaseOrders: true,
    basicReports: true,
    pdfExport: true,
    excelExport: true,
    userPermissions: true,
    attachments: true,
    aiFeatures: true,
    analytics: true,
    customerAging: false,
    inventoryValuation: false,
    advancedReports: true,
    collaboration: false,
    approvalWorkflows: false,
    maxUsers: 1,
    canEditCategories: true,
    canEditUnits: false,
    canEditInventory: true,
    basicLedger: true,
    advancedSales: true,
    advancedPurchases: true,
    paymentManagement: true,
    securitySettings: false,
    // Reports access
    trialBalance: "full",
    balanceSheet: "view",
    profitLoss: "view",
    postings: "view",
    cashflow: false,
    chartOfAccounts: "view",
    taxReports: "basic",
    vat: "basic",
    // Report Categories
    salesReports: "basic", // Basic sales analysis
    purchaseReports: "basic", // Basic purchase analysis
    inventoryReports: "basic", // Basic inventory status
    financialReports: "standard", // Trial balance, basic P&L, balance sheet
    taxationReports: "basic", // Basic tax reports
    customerReports: "basic", // Basic customer reports
    vendorReports: "basic", // Basic vendor reports
  },
  [SUBSCRIPTION_PLANS.TEAM]: {
    dashboard: true,
    basicSales: true,
    basicPurchases: true,
    basicInventory: true,
    invoices: true,
    quotes: "full",
    purchaseOrders: true,
    basicReports: true,
    pdfExport: true,
    excelExport: true,
    userPermissions: true,
    attachments: true,
    aiFeatures: true,
    analytics: true,
    customerAging: true,
    inventoryValuation: true,
    advancedReports: true,
    collaboration: true,
    approvalWorkflows: true,
    maxUsers: "unlimited",
    canEditCategories: true,
    canEditUnits: false,
    canEditInventory: true,
    basicLedger: true,
    advancedSales: true,
    advancedPurchases: true,
    paymentManagement: true,
    adminFeatures: true,
    securitySettings: false,
    // Reports access
    trialBalance: "full",
    balanceSheet: "full",
    profitLoss: "full",
    postings: "full",
    cashflow: "view",
    chartOfAccounts: "full",
    taxReports: "advanced",
    vat: "advanced",
    // Report Categories
    salesReports: "advanced", // Advanced sales analysis with trends
    purchaseReports: "advanced", // Advanced purchase analysis with forecasting
    inventoryReports: "advanced", // Advanced inventory analytics
    financialReports: "advanced", // All financial reports with analysis
    taxationReports: "advanced", // Detailed tax analysis and planning
    customerReports: "advanced", // Customer aging and detailed analysis
    vendorReports: "advanced", // Vendor aging and detailed analysis
  },
  [SUBSCRIPTION_PLANS.ENTERPRISE]: {
    dashboard: true,
    basicSales: true,
    basicPurchases: true,
    basicInventory: true,
    invoices: true,
    quotes: "full",
    purchaseOrders: true,
    basicReports: true,
    pdfExport: true,
    excelExport: true,
    userPermissions: true,
    attachments: true,
    aiFeatures: true,
    analytics: true,
    customerAging: true,
    inventoryValuation: true,
    advancedReports: true,
    collaboration: true,
    approvalWorkflows: true,
    maxUsers: "unlimited",
    canEditCategories: true,
    canEditUnits: true,
    canEditInventory: true,
    basicLedger: true,
    advancedSales: true,
    advancedPurchases: true,
    paymentManagement: true,
    adminFeatures: true,
    apiAccess: true,
    sso: true,
    customIntegrations: true,
    prioritySupport: true,
    securitySettings: true,
    // Reports access
    trialBalance: "full",
    balanceSheet: "full",
    profitLoss: "full",
    postings: "full",
    cashflow: "full",
    chartOfAccounts: "full",
    taxReports: "full",
    vat: "full",
    // Report Categories
    salesReports: "full", // Custom sales reports and real-time analytics
    purchaseReports: "full", // Custom purchase reports and analytics
    inventoryReports: "full", // Custom inventory reports and analytics
    financialReports: "full", // Custom financial reports and analytics
    taxationReports: "full", // Custom tax reports and compliance
    customerReports: "full", // Custom customer reports and insights
    vendorReports: "full", // Custom vendor reports and insights
  },
};

export const useSubscription = () => {
  const { user } = useAuth();

  const getSubscriptionPlan = () => {
    // If user is not loaded yet, return FREE plan
    if (!user) {
      return SUBSCRIPTION_PLANS.FREE;
    }
    
    const plan = user.subscriptionPlan || SUBSCRIPTION_PLANS.FREE;
    
    // Validate that the plan exists in PLAN_FEATURES
    if (!PLAN_FEATURES[plan]) {
      console.warn(`Invalid subscription plan: ${plan}, defaulting to FREE`);
      return SUBSCRIPTION_PLANS.FREE;
    }
    
    return plan;
  };

  const getSubscriptionStatus = () => {
    return user?.subscriptionStatus || "active";
  };

  // Plan gating removed. Every authenticated user has access to every
  // feature; per-org page-level access lives on the SA-managed pageRoles
  // map instead of the subscription plan enum.
  const hasSubscriptionAccess = () => !!user;
  const canAccessFeature = () => !!user;

  const getPlanFeatures = () => {
    const plan = getSubscriptionPlan();
    return PLAN_FEATURES[plan] || PLAN_FEATURES[SUBSCRIPTION_PLANS.FREE];
  };

  const isPlanActive = (planName) => {
    return getSubscriptionPlan() === planName && hasSubscriptionAccess();
  };

  const canAccessRoute = () => !!user;

  // Legacy route-access table kept behind a dead-code guard so we can
  // re-enable per-plan gating later if the SaaS billing story comes
  // back. `canAccessRoute` above short-circuits before it's read.
  // eslint-disable-next-line no-unused-vars
  const _legacyRouteAccess = (routePath) => {
    if (user?.role === "super_admin") return true;
    const plan = getSubscriptionPlan();
    const routeAccess = {
      [SUBSCRIPTION_PLANS.FREE]: [
        "/customers",
        "/vendors",
        "/quotes", // View only
        "/purchases/requisitions", // Create only
        "/inventory/list", // View only
        "/categories", // View only
        "/settings/units", // View only
        "/reports/trial-balance",
        "/reports/ledger",
      ],
      [SUBSCRIPTION_PLANS.PRO]: [
        "/dashboard",
        "/sales/invoices",
        "/sales/orders",
        "/payment",
        "/accounts/credit-notes",
        "/purchases/quotes",
        "/purchases/bills",
        "/purchases/returns",
        "/inventory/adjustments",
        "/inventory/transfers",
        "/reports/profit-loss",
        "/reports/balance-sheet",
        "/reports/cashflow",
        "/reports/chart-of-accounts",
        "/reports/tax",
        "/reports/vat",
        "/reports/postings",
      ],
      [SUBSCRIPTION_PLANS.TEAM]: [
        "/admin/users",
        "/admin/organizations",
        "/admin/branches",
        "/admin/departments",
        "/admin/authority",
        "/purchases/requisitions/issuance",
        "/purchases/grn",
        "/inventory/reports",
        "/inventory/dashboard",
        "/purchases/reports",
        "/purchases/dashboard",
        "/reports/sales",
        "/reports/customers/aging",
        "/reports/vendors/aging",
        "/reports",
        "/inventory/valuations",
      ],
    };

    // Get all accessible routes based on the subscription plan
    let accessibleRoutes = [];

    // Add routes from lower tiers
    if (plan === SUBSCRIPTION_PLANS.ENTERPRISE) {
      accessibleRoutes = [
        ...routeAccess[SUBSCRIPTION_PLANS.FREE],
        ...routeAccess[SUBSCRIPTION_PLANS.PRO],
        ...routeAccess[SUBSCRIPTION_PLANS.TEAM],
      ];
    } else if (plan === SUBSCRIPTION_PLANS.TEAM) {
      accessibleRoutes = [
        ...routeAccess[SUBSCRIPTION_PLANS.FREE],
        ...routeAccess[SUBSCRIPTION_PLANS.PRO],
        ...routeAccess[SUBSCRIPTION_PLANS.TEAM],
      ];
    } else if (plan === SUBSCRIPTION_PLANS.PRO) {
      accessibleRoutes = [
        ...routeAccess[SUBSCRIPTION_PLANS.FREE],
        ...routeAccess[SUBSCRIPTION_PLANS.PRO],
      ];
    } else {
      accessibleRoutes = routeAccess[SUBSCRIPTION_PLANS.FREE];
    }

    // Check if the route is accessible
    return accessibleRoutes.some((route) => routePath.startsWith(route));
  };

  const canEdit = () => !!user;

  const getUpgradeMessage = (featureName) => {
    const plan = getSubscriptionPlan();
    if (plan === SUBSCRIPTION_PLANS.FREE) {
      return `This feature requires a Pro plan or higher. Upgrade to access ${featureName}.`;
    }
    if (plan === SUBSCRIPTION_PLANS.PRO) {
      return `This feature requires a Team plan or higher. Upgrade to access ${featureName}.`;
    }
    return `This feature is not available in your current plan.`;
  };

  return {
    getSubscriptionPlan,
    getSubscriptionStatus,
    hasSubscriptionAccess,
    canAccessFeature,
    getPlanFeatures,
    isPlanActive,
    canAccessRoute,
    canEdit,
    getUpgradeMessage,
    SUBSCRIPTION_PLANS,
    PLAN_FEATURES,
  };
};
